/**
 * Single `useAuth()` for the whole app, dispatching on the build-time IdP.
 *
 *   cognito / entra → react-oidc-context (authorization code + PKCE)
 *   google          → the GIS shim in googleAuth.tsx
 *
 * Every component imports from HERE rather than from "react-oidc-context", so
 * the ~22 call sites are IdP-agnostic and none of them had to change beyond the
 * import path. See googleAuth.tsx for why Google cannot use the same library.
 *
 * The two implementations are not interchangeable at runtime — the provider in
 * main.tsx must match. Selecting both from the same build-time flag is what
 * keeps them in step.
 */
import { useAuth as useOidcAuth } from "react-oidc-context";
import { isGoogle } from "./idp";
import { useGoogleAuth } from "./googleAuth";

// Deliberately `any`-shaped at the boundary: react-oidc's AuthContextProps and
// GoogleAuthState overlap on everything the app touches but are not the same
// type, and widening either one would mean editing all 22 call sites to satisfy
// the compiler for no behavioural gain.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useAuth = (): any => (
  // Hooks must not be called conditionally — but `isGoogle` is a build-time
  // constant, so exactly one branch exists in any given bundle and the call
  // order is stable for the lifetime of the app.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  isGoogle ? useGoogleAuth() : useOidcAuth()
);

export default useAuth;
